import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, FolderTree, GripVertical, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTeamTree, useReparentTeam } from "@/hooks/queries/use-teams";
import { useAuthStore } from "@/stores/auth-store";
import { useToast } from "@/hooks/useToast";
import { getTeamHueColor, getTeamIconPath } from "@/lib/team-hue";
import { TEAM_LEVEL_ORDER, findTeamLineage, flattenTeamTree, getTeamLevelBadgeClass, getTeamLevelLabel } from "@/lib/team-hierarchy";
import { cn } from "@/lib/utils";
import type { TeamTreeNode } from "@/lib/api/types";

const STORAGE_KEY = "teams.tree.expanded";

interface TeamTreeSidebarProps {
  currentTeamId?: string;
  className?: string;
  onNavigate?: () => void;
}

interface TeamTreeRowProps {
  node: TeamTreeNode;
  depth: number;
  expandedIds: Set<string>;
  currentTeamId: string | undefined;
  canReparent: boolean;
  draggedTeamId: string | null;
  dragOverId: string | null;
  onToggle: (teamId: string) => void;
  onDragStart: (teamId: string) => void;
  onDragEnd: () => void;
  onDragEnter: (teamId: string) => void;
  onDrop: (parentId: string) => void;
  canDropOn: (parentId: string) => boolean;
  onNavigate: (() => void) | undefined;
}

function TeamTreeRow({
  node,
  depth,
  expandedIds,
  currentTeamId,
  canReparent,
  draggedTeamId,
  dragOverId,
  onToggle,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDrop,
  canDropOn,
  onNavigate,
}: TeamTreeRowProps) {
  const isExpanded = expandedIds.has(node.id);
  const isActive = currentTeamId === node.id;
  const isDragTarget = dragOverId === node.id && canDropOn(node.id);
  const hueColor = getTeamHueColor(node.name);
  const iconPath = getTeamIconPath(node.name);
  const canShowChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-2 rounded-xl border px-2 py-2 transition-colors",
          isActive
            ? "border-primary/30 bg-primary/10 text-foreground"
            : "border-transparent bg-transparent text-muted-foreground hover:border-white/[0.06] hover:bg-white/[0.04] hover:text-foreground",
          isDragTarget && "border-primary/40 bg-primary/10",
        )}
        style={{ paddingLeft: `${String(depth * 12 + 8)}px` }}
        draggable={canReparent}
        onDragStart={() => onDragStart(node.id)}
        onDragEnd={onDragEnd}
        onDragOver={(event) => {
          if (!canDropOn(node.id)) return;
          event.preventDefault();
        }}
        onDragEnter={() => onDragEnter(node.id)}
        onDrop={(event) => {
          if (!canDropOn(node.id)) return;
          event.preventDefault();
          onDrop(node.id);
        }}
      >
        {canReparent ? <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/60" /> : null}
        {canShowChildren ? (
          <button
            type="button"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
            onClick={() => onToggle(node.id)}
            aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform", !isExpanded && "-rotate-90")} />
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}

        <Link
          to={`/teams/${node.id}`}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${hueColor}15` }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d={iconPath} stroke={hueColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{node.name}</span>
              {node.canOwnEpics ? <Sparkles className="h-3.5 w-3.5 shrink-0 text-emerald-300" /> : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={cn("border px-2 py-0 text-[10px] font-medium", getTeamLevelBadgeClass(node.level))}>
                {getTeamLevelLabel(node.level)}
              </Badge>
              {node.canOwnEpics ? (
                <span className="text-[10px] text-emerald-300">Epic owner</span>
              ) : node.childCount ? (
                <span className="text-[10px] text-muted-foreground">{node.childCount} children</span>
              ) : null}
            </div>
          </div>
        </Link>
      </div>

      {canShowChildren && isExpanded ? (
        <div className="mt-1 space-y-1">
          {node.children.map((child) => (
            <TeamTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              currentTeamId={currentTeamId}
              canReparent={canReparent}
              draggedTeamId={draggedTeamId}
              dragOverId={dragOverId}
              onToggle={onToggle}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragEnter={onDragEnter}
              onDrop={onDrop}
              canDropOn={canDropOn}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function readStoredExpandedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
}

export function TeamTreeSidebar({ currentTeamId, className, onNavigate }: TeamTreeSidebarProps) {
  const { data: tree, isLoading } = useTeamTree();
  const reparentTeam = useReparentTeam();
  const { toast } = useToast();
  const isAdmin = useAuthStore((state) => state.user?.isGlobalAdmin ?? false);
  const [expandedIds, setExpandedIds] = useState<string[]>(() => readStoredExpandedIds());
  const [draggedTeamId, setDraggedTeamId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const flattenedTree = useMemo(() => flattenTeamTree(tree ?? []), [tree]);
  const nodeMap = useMemo(() => new Map(flattenedTree.map((node) => [node.id, node])), [flattenedTree]);
  const descendantMap = useMemo(() => {
    const map = new Map<string, Set<string>>();

    const build = (node: TeamTreeNode): Set<string> => {
      const descendants = new Set<string>();
      node.children.forEach((child) => {
        descendants.add(child.id);
        build(child).forEach((descendantId) => descendants.add(descendantId));
      });
      map.set(node.id, descendants);
      return descendants;
    };

    (tree ?? []).forEach(build);
    return map;
  }, [tree]);

  useEffect(() => {
    if (!tree?.length) return;
    if (expandedIds.length > 0) return;
    setExpandedIds(tree.map((node) => node.id));
  }, [expandedIds.length, tree]);

  useEffect(() => {
    if (!tree?.length || !currentTeamId) return;
    const lineage = findTeamLineage(tree, currentTeamId).slice(0, -1);
    if (lineage.length === 0) return;
    setExpandedIds((prev) => Array.from(new Set([...prev, ...lineage])));
  }, [currentTeamId, tree]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedIds));
    } catch {
      // Ignore localStorage availability issues.
    }
  }, [expandedIds]);

  const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  const toggleExpanded = (teamId: string) => {
    setExpandedIds((prev) => (
      prev.includes(teamId)
        ? prev.filter((value) => value !== teamId)
        : [...prev, teamId]
    ));
  };

  const canDropOn = (parentId: string | null) => {
    if (!isAdmin || !draggedTeamId) return false;
    if (parentId === draggedTeamId) return false;
    if (parentId && descendantMap.get(draggedTeamId)?.has(parentId)) return false;

    const draggedNode = nodeMap.get(draggedTeamId);
    if (!draggedNode?.level) return Boolean(parentId === null || parentId);
    if (parentId === null) return draggedNode.level === "group";

    const parentNode = nodeMap.get(parentId);
    if (!parentNode?.level) return false;
    return TEAM_LEVEL_ORDER[parentNode.level] < TEAM_LEVEL_ORDER[draggedNode.level];
  };

  const handleDrop = async (parentId: string | null) => {
    if (!draggedTeamId || !canDropOn(parentId)) return;

    try {
      await reparentTeam.mutateAsync({ id: draggedTeamId, parentId });
      toast(parentId ? "Team moved in hierarchy" : "Team moved to the top level");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to move team");
    } finally {
      setDraggedTeamId(null);
      setDragOverId(null);
    }
  };

  return (
    <aside className={cn("glass-card rounded-2xl p-4", className)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <FolderTree className="h-4 w-4 text-primary" />
            Team hierarchy
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Browse the org tree by group, product line, product, and discipline.
          </p>
        </div>
        {expandedIds.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => setExpandedIds([])}
          >
            Collapse all
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-12 animate-pulse rounded-xl bg-muted/60" />
          ))}
        </div>
      ) : !tree?.length ? (
        <div className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-sm text-muted-foreground">
          No teams available yet.
        </div>
      ) : (
        <div
          className={cn(
            "space-y-1 rounded-2xl border border-transparent p-1 transition-colors",
            dragOverId === "root" && canDropOn(null) && "border-primary/40 bg-primary/5",
          )}
          onDragOver={(event) => {
            if (!canDropOn(null)) return;
            event.preventDefault();
          }}
          onDragEnter={() => setDragOverId("root")}
          onDrop={(event) => {
            if (!canDropOn(null)) return;
            event.preventDefault();
            void handleDrop(null);
          }}
        >
          {isAdmin && draggedTeamId ? (
            <div className="mb-2 rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-3 py-2 text-xs text-muted-foreground">
              Drop on a team to reparent, or drop here to move a group to the top level.
            </div>
          ) : null}
          {tree.map((node) => (
            <TeamTreeRow
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedSet}
              currentTeamId={currentTeamId}
              canReparent={isAdmin}
              draggedTeamId={draggedTeamId}
              dragOverId={dragOverId}
              onToggle={toggleExpanded}
              onDragStart={(teamId) => setDraggedTeamId(teamId)}
              onDragEnd={() => {
                setDraggedTeamId(null);
                setDragOverId(null);
              }}
              onDragEnter={(teamId) => setDragOverId(teamId)}
              onDrop={(parentId) => {
                void handleDrop(parentId);
              }}
              canDropOn={canDropOn}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </aside>
  );
}
